import { Component, EventEmitter, OnInit, Output, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { InvestmentValidationService } from './investment-validation.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-investment-validation',
    templateUrl: './investment-validation.component.html',
    styleUrls: ['./investment-validation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class InvestmentValidationComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  modalTitle = 'Investments Validation';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  loadingData = true;

  // Entity data properties
  entityList: any[] = [];
  leId: string = '';
  cdr_no: string = '';
  leType: string = '';

  // Component-specific properties
  selectedGridRow: any[] = [];
  sysAcctInvstMappingDtls: any[] = [];
  investmentTypeDropdown: any[] = [];
  investmentTypeDropdownDisplay: any[] = [];
  investmentTagDtls: any[] = [];
  totalBreakout: number = 0;
  totalTBBSBalanceAmt: number = 0;
  totalInvAmount: number = 0;
  issueId: number = 0;

  // Data loading optimization
  private dataLoadingStates = {
    entityData: false,
    formData: false
  };

  filterParams: any = {};
  private headerData: any = {};
  private globalParams: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};

  private isInitialized = false;
  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private investmentValidationService: InvestmentValidationService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const fp = normalizeFilterParams(activeScreen) || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const gc = generalConfigFromGlobal(this.gtwGlobal) || {};
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url ?? gc.api ?? '' };
    this.headerData = header;
    this.headerData.workspaceFactory = this.gtwWorkspace.workspaceFactory;
    this.globalParams = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = fp;
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
  }

  ngOnInit() {
    this.applyWorkspaceContext();

    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.isInitialized = false;
        this.init();
        this.cdr.detectChanges();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.isInitialized = false;
        this.init();
        this.cdr.detectChanges();
      })
    );

    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.setupEntityData();
      this.showFormLoader = false;
      this.init();
    } else {
      this.showFormLoader = true;
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  init() {
    try {
      if (this.isInitialized) return;

      if (this.rowData && Object.keys(this.rowData).length > 0 && 
          this.GENERAL_CONFIG && this.GENERAL_CONFIG.base_url &&
          this.filterParams && Object.keys(this.filterParams).length > 0) {
        
        this.isInitialized = true;
        this.setupEntityData();
        
        this.getInvestmentTypes();
        this.checkLockStatus();
        
        if (this.rowData.COMBINATION_KEY) {
          this.issueId = this.rowData.ISSUE_KEY || 0;
          this.selectedGridRow = [this.rowData];
          this.getInvestmentTagDtls(this.rowData.COMBINATION_KEY);
        }
      }
    } catch (e) {
      console.error('Error in Investment Validation init:', e);
      this.showFormLoader = false;
    }
  }

  private setupEntityData() {
    // Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.CDR_NO;
    this.leType = this.rowData.LE_TYPE;

    // Entity list with proper fields for FTC component
    this.entityList = [
      {key: "LEID", value: this.rowData.LEID},
      {key: "Tax Code", value: this.rowData.CDR_NO},
      {key: "Reporting Period", value: this.rowData.REPORTING_PERIOD},
      {key: "ME Code", value: this.rowData.ME_CODE},
      {key: "ME Name", value: this.rowData.ME_NAME}
    ];

    this.dataLoadingStates.entityData = true;
    this.checkAllDataLoaded();
  }

  checkAllDataLoaded() {
    const allLoaded = Object.values(this.dataLoadingStates).every(state => state);
    if (allLoaded) {
      this.showFormLoader = false;
      this.loadingData = false;
      this.cdr.detectChanges();
    }
  }

  // Check lock status
  checkLockStatus() {
    if (!this.GENERAL_CONFIG || !this.GENERAL_CONFIG.base_url) {
      console.log('Lock status check skipped - missing GENERAL_CONFIG or base_url');
      return;
    }
    
    if (!this.filterParams || !this.filterParams.scenario) {
      console.log('Lock status check skipped - missing filterParams.scenario');
      return;
    }

    // Merge filterParams with the request params
    const lockParams = _.merge({}, this.filterParams, {
      "action_code": 'n10cpv',
      "p_scenario": this.filterParams.scenario,
      "p_jcd_key": this.globalParams?.jcd_key || this.rowData.JCD_KEY
    });

    this.jsonObjectFactory.getJSONObj(this.GENERAL_CONFIG.base_url + '/loadJsonObject', lockParams, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          if (data && data.jsonObject && data.jsonObject[0] && data.jsonObject[0].LOCK_STATUS === 'Y') {
            this._snackBar.open(`Tax Return has already been filed for tax year ${this.filterParams.tax_year}.`, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
            this.cancel();
          }
        },
        (error: any) => {
          console.error('Error checking lock status:', error);
        }
      );
  }

  // Get investment types dropdown
  getInvestmentTypes() {
    if (!this.GENERAL_CONFIG || !this.GENERAL_CONFIG.base_url) {
      console.log('Investment types API skipped - missing GENERAL_CONFIG or base_url');
      return;
    }
    
    if (!this.filterParams || !this.filterParams.tax_year) {
      console.log('Investment types API skipped - missing filterParams.tax_year');
      return;
    }

    // Merge filterParams with the request params
    const params = _.merge({}, this.filterParams, {
      "action_code": 'ef4ghx',
      "tax_year": this.filterParams.tax_year
    });

    this.jsonObjectFactory.getJSONObj(this.GENERAL_CONFIG.base_url + '/loadJsonObject', params, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.investmentTypeDropdown = data.jsonObject || [];
          this.investmentTypeDropdownDisplay = _.clone(this.investmentTypeDropdown);
          _.remove(this.investmentTypeDropdownDisplay, { IS_DISPLAY: 'N' });
          this.cdr.detectChanges();
        },
        (error: any) => {
          console.error('Error loading investment types:', error);
        }
      );
  }

  // Get investment tag details
  getInvestmentTagDtls(cc_key: any) {
    if (!this.GENERAL_CONFIG || !this.GENERAL_CONFIG.base_url) {
      console.log('Investment tag details API skipped - missing GENERAL_CONFIG or base_url');
      return;
    }

    // Merge filterParams with the request params
    const params = _.merge({}, this.filterParams, {
      "action_code": '7go09e',
      "cc_key": cc_key
    });

    this.jsonObjectFactory.getJSONObj(this.GENERAL_CONFIG.base_url + '/loadJsonObject', params, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.investmentTagDtls = data.jsonObject || [];
          this.processInvestmentTagDtls();
          this.cdr.detectChanges();
        },
        (error: any) => {
          console.error('Error loading investment tag details:', error);
          this.showFormLoader = false;
          this.loadingData = false;
          this.cdr.detectChanges();
        }
      );
  }

  // Process investment tag details
  processInvestmentTagDtls() {
    if (!this.investmentTagDtls || this.investmentTagDtls.length === 0) {
      this.showFormLoader = false;
      this.loadingData = false;
      this.dataLoadingStates.formData = true;
      this.checkAllDataLoaded();
      return;
    }

    this.sysAcctInvstMappingDtls = [];
    this.totalBreakout = 0;
    this.totalTBBSBalanceAmt = 0;

    for (let key in this.investmentTagDtls) {
      const newObj: any = _.clone(this.rowData);
      newObj.ACCT = this.investmentTagDtls[key].ACCT;
      newObj.ACCT_DESC = this.investmentTagDtls[key].ACCT_DESC;
      newObj.ACCT_KEY = this.investmentTagDtls[key].ACCT_KEY;
      newObj.COMBINATION_KEY = this.investmentTagDtls[key].DEF_COMBINATION_KEY;
      newObj.INVESTMENT_TYPE = (this.investmentTagDtls[key].INV_TYPE_DESC === undefined ? '' : this.investmentTagDtls[key].INV_TYPE_DESC);
      newObj.INVESTMENT_AMT = ((this.investmentTagDtls[key].INV_TYPE_DESC === undefined && this.investmentTagDtls[key].INV_AMT === undefined) ? this.investmentTagDtls[key].INV_BALANCE : this.investmentTagDtls[key].INV_AMT);
      newObj.INV_BALANCE = this.investmentTagDtls[key].INV_BALANCE;
      newObj.INV_TYPE_KEY = '' + this.investmentTagDtls[key].INV_TYPE;
      newObj.ORG_INVESTMENT_AMT = (this.investmentTagDtls[key].INV_TYPE_DESC === undefined ? this.investmentTagDtls[key].INV_BALANCE : this.investmentTagDtls[key].INV_AMT);
      newObj.TAG_KEY = (this.investmentTagDtls[key].TAG_KEY === undefined || this.investmentTagDtls[key].TAG_KEY === null) ? null : this.investmentTagDtls[key].TAG_KEY;
      newObj.IS_DEFAULT = this.investmentTagDtls[key].IS_DEFAULT;
      
      if (this.investmentTagDtls[key].TAG_KEY === undefined || this.investmentTagDtls[key].TAG_KEY === null) {
        newObj.OPERATION_TYPE = 'I';
      } else {
        newObj.OPERATION_TYPE = 'U';
      }
      
      newObj.IS_ENABLE = 'N';
      newObj.ALT_GIE_KEY = this.investmentTagDtls[key].ALT_GIE_KEY;
      
      // Add to totalBreakout using the computed INVESTMENT_AMT (same as AngularJS line 219)
      this.totalBreakout += parseFloat(this.investmentTagDtls[key].INV_AMT);
      this.sysAcctInvstMappingDtls.push(newObj);
    }

    // Calculate total TBBS balance amount
    const uniqBalanceAmt = Array.from(this.getUniqueValues(this.sysAcctInvstMappingDtls, 'INV_BALANCE').values());
    for (let i in uniqBalanceAmt) {
      this.totalTBBSBalanceAmt += parseFloat(uniqBalanceAmt[i]);
    }

    if (isNaN(this.totalBreakout)) this.totalBreakout = 0;
    
    this.showFormLoader = false;
    this.loadingData = false;
    this.dataLoadingStates.formData = true;
    this.checkAllDataLoaded();
  }

  // Form a unique set of entries from a given array based on the Property passed
  getUniqueValues(array: any[], prop: string): Set<any> {
    const result = new Set();
    array.forEach((item) => {
      if (item.hasOwnProperty(prop)) {
        result.add(item[prop]);
      }
    });
    return result;
  }

  // Check whether the given array has duplicates
  hasDuplicates(a: any[]): boolean {
    return _.uniq(a).length !== a.length;
  }

  // Calculate the Total Breakout Amount
  getTotalBreakout(data?: any) {
    if (this.sysAcctInvstMappingDtls === undefined || this.sysAcctInvstMappingDtls === null) {
      return 0;
    }

    this.totalBreakout = 0;
    this.totalInvAmount = 0;

    // Calculate total investment amount for non-default rows
    for (let i = 0; i < this.sysAcctInvstMappingDtls.length; i++) {
      if (data !== undefined && data.ACCT === this.sysAcctInvstMappingDtls[i].ACCT && this.sysAcctInvstMappingDtls[i].IS_DEFAULT === 'N') {
        this.totalInvAmount = this.totalInvAmount + parseInt(this.sysAcctInvstMappingDtls[i].INVESTMENT_AMT === null ? 0 : this.sysAcctInvstMappingDtls[i].INVESTMENT_AMT);
      }
    }

    // Update default row and calculate total breakout
    for (let i = 0; i < this.sysAcctInvstMappingDtls.length; i++) {
      if (data !== undefined && data.ACCT === this.sysAcctInvstMappingDtls[i].ACCT && this.sysAcctInvstMappingDtls[i].IS_DEFAULT === 'Y') {
        this.sysAcctInvstMappingDtls[i].INVESTMENT_AMT = parseInt(this.sysAcctInvstMappingDtls[i].ORG_INVESTMENT_AMT) - this.totalInvAmount;
      }

      if (this.sysAcctInvstMappingDtls[i].OPERATION_TYPE !== 'D') {
        this.totalBreakout += parseFloat(this.sysAcctInvstMappingDtls[i].INVESTMENT_AMT !== null ? this.sysAcctInvstMappingDtls[i].INVESTMENT_AMT : 0);
      }
    }

    if (isNaN(this.totalBreakout)) this.totalBreakout = 0;
    this.cdr.detectChanges();
    return parseInt(this.totalBreakout.toString());
  }

  // Add a new row
  addRow(selectedRowData: any) {
    const index = this.sysAcctInvstMappingDtls.indexOf(selectedRowData);
    const tempObj = _.clone(selectedRowData);
    tempObj.OPERATION_TYPE = 'I';
    tempObj.INVESTMENT_TYPE = undefined;
    tempObj.INV_TYPE_KEY = null;
    tempObj.INVESTMENT_AMT = null;
    tempObj.TAG_KEY = null;
    tempObj.IS_DEFAULT = 'N';
    tempObj.IS_ENABLE = 'Y';
    
    this.sysAcctInvstMappingDtls.splice(index + 1, 0, tempObj);
    this.getTotalBreakout();
    this.cdr.detectChanges();
  }

  // Delete a row
  deleteRow(selectedRowData: any) {
    const index = this.sysAcctInvstMappingDtls.indexOf(selectedRowData);
    
    if (this.sysAcctInvstMappingDtls[index].OPERATION_TYPE !== undefined && 
        this.sysAcctInvstMappingDtls[index].OPERATION_TYPE !== null &&
        this.sysAcctInvstMappingDtls[index].OPERATION_TYPE !== 'I') {
      this.sysAcctInvstMappingDtls[index].OPERATION_TYPE = 'D';
    } else {
      this.sysAcctInvstMappingDtls.splice(index, 1);
    }

    // Update default row
    for (let i = 0; i < this.sysAcctInvstMappingDtls.length; i++) {
      if (selectedRowData.ACCT === this.sysAcctInvstMappingDtls[i].ACCT && this.sysAcctInvstMappingDtls[i].IS_DEFAULT === 'Y') {
        this.sysAcctInvstMappingDtls[i].ORG_INVESTMENT_AMT = this.sysAcctInvstMappingDtls[i].INVESTMENT_AMT;
      }
    }

    this.getTotalBreakout();

    // Add a new empty row if no accounts left
    const countAcct = _.filter(this.sysAcctInvstMappingDtls, (eachRow) => eachRow.OPERATION_TYPE !== 'D').length;
    if (countAcct === 0) {
      const tempObj = _.clone(selectedRowData);
      tempObj.OPERATION_TYPE = 'I';
      tempObj.INVESTMENT_TYPE = null;
      tempObj.INV_TYPE_KEY = null;
      tempObj.INVESTMENT_AMT = null;
      tempObj.TAG_KEY = null;
      this.sysAcctInvstMappingDtls.splice(index + 1, 0, tempObj);
    }
    this.cdr.detectChanges();
  }

  // Save investment data
  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    // Validation: Check if all required fields are filled
    for (let row in this.sysAcctInvstMappingDtls) {
      if ((this.sysAcctInvstMappingDtls[row].INV_TYPE_KEY === undefined ||
           this.sysAcctInvstMappingDtls[row].INV_TYPE_KEY === null ||
           this.sysAcctInvstMappingDtls[row].INV_TYPE_KEY === '' ||
           this.sysAcctInvstMappingDtls[row].INVESTMENT_AMT === undefined ||
           this.sysAcctInvstMappingDtls[row].INVESTMENT_AMT === null) &&
           this.sysAcctInvstMappingDtls[row].IS_DEFAULT === 'N') {
        this._snackBar.open('Please select/enter all the required fields.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.isSaveClicked = false;
        this.crudLoading = false;
        return;
      }
    }

    // Check for duplicate investment types per account
    const uniqAcctKeys = Array.from(this.getUniqueValues(this.sysAcctInvstMappingDtls, 'ACCT').values());
    for (let i in uniqAcctKeys) {
      const allInvAmts: any[] = [];
      for (let row in this.sysAcctInvstMappingDtls) {
        if (this.sysAcctInvstMappingDtls[row].ACCT === uniqAcctKeys[i] && this.sysAcctInvstMappingDtls[row].OPERATION_TYPE !== 'D') {
          allInvAmts.push(this.sysAcctInvstMappingDtls[row].INV_TYPE_KEY);
        }
      }
      if (this.hasDuplicates(allInvAmts)) {
        this._snackBar.open(`Please select different Investment Types for System Account - ${uniqAcctKeys[i]}`, 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.isSaveClicked = false;
        this.crudLoading = false;
        return;
      }
    }

    // Prepare data to save
    const dataToSave: any[] = [];
    for (let row in this.sysAcctInvstMappingDtls) {
      if (this.sysAcctInvstMappingDtls[row].IS_DEFAULT === 'N' ||
          (this.sysAcctInvstMappingDtls[row].IS_DEFAULT === 'Y' && this.sysAcctInvstMappingDtls[row].INVESTMENT_TYPE === '')) {
        const returnObj: any = {};
        returnObj.ACCT_KEY = this.sysAcctInvstMappingDtls[row].ACCT_KEY;
        returnObj.COMBINATION_KEY = this.sysAcctInvstMappingDtls[row].COMBINATION_KEY;
        returnObj.INVESTMENT_TYPE = (this.sysAcctInvstMappingDtls[row].INV_TYPE_KEY === undefined ? null : this.sysAcctInvstMappingDtls[row].INV_TYPE_KEY);
        returnObj.AMOUNT = parseInt(this.sysAcctInvstMappingDtls[row].INVESTMENT_AMT);
        returnObj.TAG_KEY = (this.sysAcctInvstMappingDtls[row].TAG_KEY === undefined || this.sysAcctInvstMappingDtls[row].TAG_KEY === null) ? null : this.sysAcctInvstMappingDtls[row].TAG_KEY;
        returnObj.OPERATION_TYPE = this.sysAcctInvstMappingDtls[row].OPERATION_TYPE;
        returnObj.ALT_GIE_KEY = this.sysAcctInvstMappingDtls[row].ALT_GIE_KEY;
        dataToSave.push(returnObj);
      }
    }

    const returnClobSettingsObj: any = {};
    returnClobSettingsObj.sso_id = this.USER_SETTINGS?.sso_id || this.globalParams?.sso_id || '';
    returnClobSettingsObj.object_id = Array.from(this.getUniqueValues(dataToSave, 'COMBINATION_KEY').values()).join();

    const message = 'Investment Amount(s) have been saved/updated';

    // Call service to save data
    this.investmentValidationService.saveData(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      dataToSave,
      this.issueId,
      this.globalParams,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open(message, 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        console.error('Error saving investment data:', error);
        this.cdr.detectChanges();
      }
    );
  }

  // Cancel and close modal
  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  // Track by function for ngFor optimization
  trackByFn(index: number, item: any): any {
    return item.TAG_KEY || item.ACCT + index;
  }

  // Get sorted and filtered investment mapping details
  get sortedSysAcctInvstMappingDtls(): any[] {
    if (!this.sysAcctInvstMappingDtls || this.sysAcctInvstMappingDtls.length === 0) {
      return [];
    }
    
    // Filter out deleted items and sort by ACCT (same as AngularJS orderBy:'ACCT')
    return this.sysAcctInvstMappingDtls
      .filter(item => item.OPERATION_TYPE !== 'D')
      .sort((a, b) => {
        if (a.ACCT < b.ACCT) return -1;
        if (a.ACCT > b.ACCT) return 1;
        return 0;
      });
  }
}

